/**
 * 超越 weapp-tailwindcss 的 TailwindCSS Taro 插件
 * 
 * 核心优势：
 * 1. 零配置开箱即用
 * 2. 智能类名检测和自动 safelist
 * 3. 更强大的主题系统
 * 4. 运行时动态切换
 * 5. 详细的性能分析
 * 6. 更好的错误处理和调试体验
 * 7. 支持 TailwindCSS v3/v4 自动检测
 * 8. 更全面的微信小程序兼容性
 * 9. 更智能的类名转义
 * 10. 更强大的 CSS 合法化
 */

// 导出 Vite 插件
export { createVitePlugin } from './adapters/vite'

// 导出 Webpack 插件
export { createWebpackPlugin } from './adapters/webpack'

// 导出工具函数
export { escapeWeappClass, escapeWeappClasses, escapeWeappClassString } from './utils/escape-weapp-class'
export { legalizeWeappCss } from './utils/weapp-postcss-legalizer'
export { Logger } from './utils/logger'

// 导出类型定义
export type {
  PluginOptions,
  VitePlugin,
  WebpackPlugin,
  BuildStats,
  ClassTransformResult,
  CssTransformResult,
  ThemeConfig,
  RuntimeThemeConfig,
  PerformanceEvent,
  DebugInfo,
  PluginState
} from './types'

// 导出默认配置
export const defaultConfig = {
  platform: 'weapp' as const,
  enabled: true,
  verbose: false,
  autoDetectTailwindVersion: true,
  enableSmartClassDetection: true,
  enablePerformanceOptimization: true,
  enableHotReload: true,
  enableSourceMap: false,
  enableBundleAnalysis: false,
  enableRuntimeTheme: false,
  enableClassNameCompression: true,
  enableCssCompression: true,
  enableCache: true,
  cssSelectorReplacement: {
    root: 'page',
    universal: 'view,text'
  },
  cacheConfig: {
    dir: '.tailwindcss-taro-cache',
    maxAge: 24 * 60 * 60 * 1000, // 24小时
    maxSize: 50 * 1024 * 1024 // 50MB
  },
  performanceMonitoring: {
    enabled: false,
    threshold: 1000, // 1秒
    format: 'text' as const
  },
  debug: {
    enabled: false,
    level: 'info' as const,
    outputFile: 'tailwindcss-taro-debug.log'
  }
}

// 导出版本信息
export const version = '2.0.0'

// 导出插件信息
export const pluginInfo = {
  name: 'tailwindcss-taro',
  version,
  description: '超越 weapp-tailwindcss 的 TailwindCSS Taro 插件',
  author: 'Your Name',
  license: 'MIT',
  repository: 'https://github.com/your-username/tailwindcss-taro',
  homepage: 'https://github.com/your-username/tailwindcss-taro#readme',
  bugs: 'https://github.com/your-username/tailwindcss-taro/issues',
  keywords: [
    'tailwindcss',
    'taro',
    'weapp',
    'miniprogram',
    'css',
    'postcss',
    'vite',
    'webpack',
    'typescript'
  ],
  engines: {
    node: '>=16.0.0'
  },
  peerDependencies: {
    'tailwindcss': '^3.0.0 || ^4.0.0',
    'taro': '^3.0.0 || ^4.0.0'
  }
}

// 导出兼容性信息
export const compatibility = {
  tailwindcss: ['^3.0.0', '^4.0.0'],
  taro: ['^3.0.0', '^4.0.0'],
  platforms: ['weapp', 'h5'],
  bundlers: ['vite', 'webpack'],
  node: '>=16.0.0'
}

// 导出功能特性
export const features = {
  // 核心功能
  core: [
    '零配置开箱即用',
    '自动平台检测',
    '智能类名转义',
    'CSS 合法化处理',
    'TailwindCSS v3/v4 支持'
  ],
  // 性能优化
  performance: [
    '智能缓存机制',
    '增量构建',
    '并行处理',
    '内存优化',
    '构建分析'
  ],
  // 开发体验
  developerExperience: [
    '详细日志输出',
    '热重载支持',
    '源码映射',
    '调试工具',
    '错误恢复'
  ],
  // 高级功能
  advanced: [
    '运行时主题切换',
    '类名压缩',
    'CSS 压缩',
    '性能监控',
    '包分析'
  ],
  // 兼容性
  compatibility: [
    '微信小程序 100% 兼容',
    'H5 平台支持',
    '多种构建工具',
    'TypeScript 支持',
    'ES6+ 语法'
  ]
}

// 导出使用示例
export const examples = {
  // 基础使用
  basic: `
import { createVitePlugin } from 'tailwindcss-taro'

export default {
  plugins: [
    createVitePlugin()
  ]
}
  `,
  
  // 高级配置
  advanced: `
import { createVitePlugin } from 'tailwindcss-taro'

export default {
  plugins: [
    createVitePlugin({
      platform: 'weapp',
      verbose: true,
      enableSmartClassDetection: true,
      enablePerformanceOptimization: true,
      enableBundleAnalysis: true,
      enableRuntimeTheme: true,
      performanceMonitoring: {
        enabled: true,
        threshold: 1000
      }
    })
  ]
}
  `,
  
  // 主题配置
  theme: `
import { createVitePlugin } from 'tailwindcss-taro'

export default {
  plugins: [
    createVitePlugin({
      enableRuntimeTheme: true,
      theme: {
        extend: {
          colors: {
            primary: '#007AFF',
            secondary: '#5856D6'
          }
        }
      }
    })
  ]
}
  `
}

// 导出常见问题
export const faq = {
  '如何启用详细日志？': '设置 verbose: true 选项',
  '如何优化构建性能？': '启用 enablePerformanceOptimization 和 enableCache',
  '如何支持运行时主题切换？': '启用 enableRuntimeTheme 选项',
  '如何处理特殊类名？': '插件会自动转义和合法化所有类名',
  '如何调试构建问题？': '启用 debug.enabled 和 verbose 选项',
  '如何分析构建产物？': '启用 enableBundleAnalysis 选项',
  '如何监控性能？': '启用 performanceMonitoring.enabled 选项'
}

// 导出故障排除指南
export const troubleshooting = {
  '构建失败': [
    '检查 TailwindCSS 版本兼容性',
    '确认配置文件路径正确',
    '查看详细错误日志',
    '尝试清理缓存'
  ],
  '类名未生效': [
    '确认类名已正确转义',
    '检查 CSS 是否被正确处理',
    '验证选择器是否合法',
    '查看构建日志'
  ],
  '性能问题': [
    '启用性能优化选项',
    '检查缓存配置',
    '分析构建产物',
    '监控构建时间'
  ],
  '兼容性问题': [
    '确认平台设置正确',
    '检查 CSS 特性支持',
    '验证选择器兼容性',
    '查看小程序文档'
  ]
}
